import type { VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { useAddToast } from '../../../../contexts/ToastContext';
import { useShouldShowQuickImportScanMethods } from '../../../../helpers/features/quickImportScanMethods';
import FeatureFlagRequired from '../../../generic/Gates/FeatureFlagRequired';
import QualityRequired from '../../../generic/Gates/QualityRequired';
import SecurityRequired from '../../../generic/Gates/SecurityRequired';
import Redirect from '../../../generic/Redirect';
import QuickImportScanMethodsSection from '../../../shared/settings/QuickImportScanMethodsSection';
import Header from '../../../shared/settings/SettingsPageHeader';
import PageContent from '../shared/SettingsPageContent';

import { ProjectContainerIssuesSettings } from './Containers';
import ExcludedSnippetRepositories from './ExcludedSnippetRepositories';
import Licensing from './Licensing';
import ProjectSBOMPoliciesSettings from './ProjectSBOMPoliciesSettings';
import Quality from './Quality';
import Security from './Security';
import useProjectIssuesSettings from './useProjectIssuesSettings';
import useSBOMProjectSettings from './useSBOMProjectSettings';

/**
 * ProjectPoliciesSettingsContainer
 */
const ProjectPoliciesSettingsContainer: VoidFunctionComponent = () => {
  const {
    isLoading,
    isSaving,
    isSaveButtonDisabled,
    shouldShowIssues,
    projectSettingsBaseURL,
    onSave,
    licensing,
    security,
    quality,
    quickImportScanMethods,
    excludedSnippetRepositories,
    containers,
  } = useProjectIssuesSettings();
  const addToast = useAddToast();
  const sbomSettings = useSBOMProjectSettings();
  const shouldShowScanMethods = useShouldShowQuickImportScanMethods();

  if (!shouldShowIssues && !sbomSettings.canEditPolicy) {
    return (
      <Redirect to={projectSettingsBaseURL} />
    );
  }

  return (
    <PageContent isLoading={isLoading}>
      <Header heading="Issue Policies">
        <Header.Paragraph>
          Configure how Licensing, Security, Quality and SBOM are handled within this project.
        </Header.Paragraph>
        <Header.SaveButton
          isLoading={isSaving}
          isDisabled={isSaveButtonDisabled}
          onClick={() => {
            addToast({ type: 'info', content: 'Saving project settings and running issue scan...' });
            onSave();
          }}
        />
      </Header>
      {shouldShowScanMethods && (
        <QuickImportScanMethodsSection
          scopeType="project"
          {...quickImportScanMethods}
        />
      )}
      <Licensing
        {...licensing}
      />
      <SecurityRequired>
        <Security
          {...security}
        />
      </SecurityRequired>
      <QualityRequired>
        <Quality
          {...quality}
        />
      </QualityRequired>
      <ProjectSBOMPoliciesSettings {...sbomSettings} />
      <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
        <ExcludedSnippetRepositories {...excludedSnippetRepositories} />
      </FeatureFlagRequired>
      <ProjectContainerIssuesSettings {...containers} />
    </PageContent>
  );
};

export default ProjectPoliciesSettingsContainer;
